import React, { useState, useEffect, useRef } from "react";
import PantherBetScratch from "../../../assets/panther-bet-scratch.svg";
import BlueCoinHead from "../../../assets/coins/blue-coin-head.svg";
import GreenCoinHead from "../../../assets/coins/green-coin-head.svg";
import LemonCoinHead from "../../../assets/coins/lemon-coin-head.svg";
import RedCoinHead from "../../../assets/coins/red-coin-head.svg";
import SuiLogo from "../../../assets/icons/sui-sui-logo.svg";
import BlueCoinTail from "../../../assets/coins/blue-coin-tail.svg";
import GreenCoinTail from "../../../assets/coins/green-coin-tail.svg";
import LemonCoinTail from "../../../assets/coins/lemon-coin-tail.svg";
import RedCoinTail from "../../../assets/coins/red-coin-tail.svg";
import CupIcon from "../../../assets/icons/cup.svg";
import LossIcon from "../../../assets/icons/loss.svg";
import FloatingTooltip from "../../FloatingTooltip/FloatingTooltip";
import { useGameStats } from "../../../contexts/GameStatsContext";
import { generateTip } from "../../../utils/tipGenerator";
import TreasureChest from "../../../assets/icons/treasure-chest.svg";
import PantherSlash from "../../../assets/images/panther-slash.svg";
import PantherBear from "../../../assets/images/panther-bear.svg";
import PantherBetText from "../../../assets/images/panther-bet-text.svg";
import GoldCoin from "../../../assets/coins/gold-coin.svg";
import TreasureChestOpen from "../../../assets/icons/treasure-chest-open.svg";

// Type definitions
interface CoinState {
  id: number;
  selectedSide: "heads" | "tails";
  stake: number;
}

interface CoinflipGameProps {
  onFlippingStateChange?: (isFlipping: boolean) => void;
  onResultsStateChange?: (showResults: boolean) => void;
}

interface CoinConfig {
  id: number;
  image: string;
  tailImage: string;
  alt: string;
  headsOdds: number;
  tailsOdds: number;
  borderColor: string;
  headsMultiplier: number;
  tailsMultiplier: number;
}

interface FlipResult {
  coinId: number;
  result: "heads" | "tails";
  won: boolean;
  winAmount: number;
}

interface CoinCardProps {
  coin: CoinState;
  config: CoinConfig;
  showResult?: boolean;
  flipResult?: FlipResult;
}

interface ModalCoinCardProps {
  config: CoinConfig;
  isSelected: boolean;
}

interface FlipCardProps {
  coin: CoinState;
  config: CoinConfig;
  result: FlipResult;
  isCurrentlyFlipping: boolean;
  isCompleted: boolean;
}

function CoinflipGame({
  onFlippingStateChange,
  onResultsStateChange,
}: CoinflipGameProps) {
  const [selectedCoins, setSelectedCoins] = useState<number[]>([1, 2, 3, 4]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isPantherBetEnabled, setIsPantherBetEnabled] =
    useState<boolean>(false);
  const [pantherBetStake, setPantherBetStake] = useState<number>(0);
  const [coinStates, setCoinStates] = useState<CoinState[]>([
    { id: 1, selectedSide: "heads", stake: 0 },
    { id: 2, selectedSide: "heads", stake: 0 },
    { id: 3, selectedSide: "heads", stake: 0 },
    { id: 4, selectedSide: "heads", stake: 0 },
  ]);

  const [showPantherBetModal, setShowPantherBetModal] =
    useState<boolean>(false);
  const [pantherBetAnimationStep, setPantherBetAnimationStep] =
    useState<number>(0);
  const [showFourOfACoinModal, setShowFourOfACoinModal] =
    useState<boolean>(false);
  const [flipCount, setFlipCount] = useState<number>(0);

  //for flip animation and results
  const [isFlipping, setIsFlipping] = useState<boolean>(false);
  const [currentFlippingIndex, setCurrentFlippingIndex] = useState<number>(-1);
  const [flipResults, setFlipResults] = useState<FlipResult[]>([]);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [pointsClaimed, setPointsClaimed] = useState<boolean>(false);

  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStartX, setDragStartX] = useState<number>(0);
  const [handlePosition, setHandlePosition] = useState<number>(0);
  const sliderRef = useRef<HTMLDivElement>(null);
  const [sliderProgress, setSliderProgress] = useState<number>(0);

  useEffect(() => {
    onFlippingStateChange?.(isFlipping);
  }, [isFlipping, onFlippingStateChange]);

  useEffect(() => {
    onResultsStateChange?.(showResults);
  }, [showResults, onResultsStateChange]);

  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipData, setTooltipData] = useState<{
    message: string;
    tip?: string;
  } | null>(null);
  const { stats, updateStats } = useGameStats();

  const coinConfigs: CoinConfig[] = [
    {
      id: 1,
      image: BlueCoinHead,
      tailImage: BlueCoinTail,
      alt: "Blue Coin",
      headsOdds: 80,
      tailsOdds: 20,
      borderColor: "#229EFF",
      headsMultiplier: 1.2,
      tailsMultiplier: 3,
    },
    {
      id: 2,
      image: GreenCoinHead,
      tailImage: GreenCoinTail,
      alt: "Green Coin",
      headsOdds: 70,
      tailsOdds: 30,
      borderColor: "#00CEC8",
      headsMultiplier: 1.4,
      tailsMultiplier: 2.5,
    },
    {
      id: 3,
      image: LemonCoinHead,
      tailImage: LemonCoinTail,
      alt: "Lemon Coin",
      headsOdds: 60,
      tailsOdds: 40,
      borderColor: "#00C159",
      headsMultiplier: 1.6,
      tailsMultiplier: 2.2,
    },
    {
      id: 4,
      image: RedCoinHead,
      tailImage: RedCoinTail,
      alt: "Red Coin",
      headsOdds: 50,
      tailsOdds: 50,
      borderColor: "#FF3B30",
      headsMultiplier: 1.996,
      tailsMultiplier: 1.996,
    },
  ];

  const getCoinImageForSide = (
    config: CoinConfig,
    side: "heads" | "tails"
  ): string => {
    return side === "heads" ? config.image : config.tailImage;
  };

  const handleSideSelection = (
    coinId: number,
    side: "heads" | "tails"
  ): void => {
    setCoinStates((prev) =>
      prev.map((coin) =>
        coin.id === coinId ? { ...coin, selectedSide: side } : coin
      )
    );
  };

  const handleStakeChange = (coinId: number, stake: string): void => {
    setCoinStates((prev) =>
      prev.map((coin) =>
        coin.id === coinId ? { ...coin, stake: parseFloat(stake) || 0 } : coin
      )
    );
  };

  const handlePantherBetStakeChange = (stake: string): void => {
    setPantherBetStake(parseFloat(stake) || 0);
  };

  const calculatePotentialWin = (
    stake: number,
    side: "heads" | "tails",
    coinId: number
  ): string => {
    const config = coinConfigs.find((c) => c.id === coinId);
    if (!config) return "0.00";

    const multiplier =
      side === "heads" ? config.headsMultiplier : config.tailsMultiplier;
    return (stake * multiplier).toFixed(2);
  };

  const calculatePantherBetPotentialWin = (): string => {
    return (pantherBetStake * 50).toFixed(2);
  };

  const getCoinsText = (): string => {
    const count = selectedCoins.length;
    if (count === 1) return "You are playing 1 coin";
    return `You are playing ${count} coins`;
  };

  const handleCoinToggle = (coinId: number): void => {
    setSelectedCoins((prev) => {
      if (prev.includes(coinId)) {
        return prev.length > 1 ? prev.filter((id) => id !== coinId) : prev;
      } else {
        return [...prev, coinId].sort();
      }
    });
  };

  const simulateFlipResult = (
    coin: CoinState,
    config: CoinConfig
  ): FlipResult => {
    if (flipCount === 1) {
      // Second flip - all tails
      const result = "tails";
      const won = result === coin.selectedSide;
      const multiplier =
        coin.selectedSide === "heads"
          ? config.headsMultiplier
          : config.tailsMultiplier;
      const winAmount = won ? coin.stake * multiplier : 0;

      return {
        coinId: coin.id,
        result,
        won,
        winAmount,
      };
    } else if (flipCount === 2) {
      // Third flip - all heads
      const result = "heads";
      const won = result === coin.selectedSide;
      const multiplier =
        coin.selectedSide === "heads"
          ? config.headsMultiplier
          : config.tailsMultiplier;
      const winAmount = won ? coin.stake * multiplier : 0;

      return {
        coinId: coin.id,
        result,
        won,
        winAmount,
      };
    } else {
      const random = Math.random() * 100;
      const result = random < config.headsOdds ? "heads" : "tails";
      const won = result === coin.selectedSide;

      const multiplier =
        coin.selectedSide === "heads"
          ? config.headsMultiplier
          : config.tailsMultiplier;
      const winAmount = won ? coin.stake * multiplier : 0;

      return {
        coinId: coin.id,
        result,
        won,
        winAmount,
      };
    }
  };

  const handleFlipCoins = (): void => {
    const activeCoins = coinStates.filter((coin) =>
      selectedCoins.includes(coin.id)
    );
    const results = activeCoins.map((coin) => {
      const config = coinConfigs.find((c) => c.id === coin.id)!;
      return simulateFlipResult(coin, config);
    });

    setFlipResults(results);
    setIsFlipping(true);
    setCurrentFlippingIndex(0);
    setFlipCount((prev) => prev + 1);

    // Scroll to flip container after state updates
    setTimeout(() => {
      const flipContainer = document.querySelector(
        ".coinflip-game__flip-container"
      );
      if (flipContainer) {
        flipContainer.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 100);

    // Start sequential flipping
    startSequentialFlipping(results.length);
  };

  const startSequentialFlipping = (totalCoins: number): void => {
    let currentIndex = 0;

    const flipNext = () => {
      if (currentIndex < totalCoins) {
        setCurrentFlippingIndex(currentIndex);

        setTimeout(() => {
          if (window.innerWidth <= 768) {
            const activeCard = document.querySelector(
              ".coinflip-game__flip-card.current-flip"
            );
            if (activeCard) {
              activeCard.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "center",
              });
            }
          }
        }, 100);

        setTimeout(() => {
          currentIndex++;
          if (currentIndex < totalCoins) {
            flipNext();
          } else {
            setCurrentFlippingIndex(-1);
            setTimeout(() => {
              setIsFlipping(false);

              const allTails = flipResults.every(
                (result) => result.result === "tails"
              );
              if (isPantherBetEnabled && allTails && pantherBetStake > 0) {
                setShowPantherBetModal(true);
                setPantherBetAnimationStep(0);
                startPantherBetAnimation();
              } else {
                setShowResults(true);

                setTimeout(() => {
                  const resultsContainer = document.querySelector(
                    ".coinflip-game__results-container"
                  );
                  if (resultsContainer) {
                    resultsContainer.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }
                }, 100);
              }
            }, 2000);
          }
        }, 2000);
      }
    };

    flipNext();
  };

  const startPantherBetAnimation = (): void => {
    setTimeout(() => {
      setPantherBetAnimationStep(1);
    }, 800);

    setTimeout(() => {
      setPantherBetAnimationStep(2);
    }, 2500);

    setTimeout(() => {
      setPantherBetAnimationStep(3);
    }, 5500);

    setTimeout(() => {
      setPantherBetAnimationStep(4);
    }, 7500);

    setTimeout(() => {
      setPantherBetAnimationStep(5);
    }, 9500);

    setTimeout(() => {
      setShowPantherBetModal(false);
      setPantherBetAnimationStep(0);
      setShowResults(true);
    }, 12000);
  };

  const handleSkipPantherBetAnimation = (): void => {
    setShowPantherBetModal(false);
    setPantherBetAnimationStep(0);
    setShowResults(true);
  };

  const handleSkipToResults = (): void => {
    setCurrentFlippingIndex(-1);
    setIsFlipping(false);
    const allTails = flipResults.every((result) => result.result === "tails");
    if (isPantherBetEnabled && allTails && pantherBetStake > 0) {
      setShowPantherBetModal(true);
      setPantherBetAnimationStep(0);
      startPantherBetAnimation();
    } else {
      setShowResults(true);
    }
  };

  const checkForFourOfACoin = (): boolean => {
    if (selectedCoins.length !== 4) return false;
    return flipResults.every((result) => result.won);
  };

  const handleMouseDown = (e: React.MouseEvent): void => {
    if (pointsClaimed) return;
    setIsDragging(true);
    setDragStartX(e.clientX);
  };

  const handleTouchStart = (e: React.TouchEvent): void => {
    if (pointsClaimed) return;
    setIsDragging(true);
    setDragStartX(e.touches[0].clientX);
  };

  const handleMouseMove = (e: MouseEvent): void => {
    if (!isDragging || !sliderRef.current || pointsClaimed) return;

    const sliderRect = sliderRef.current.getBoundingClientRect();
    const maxDistance = sliderRect.width - 60;
    const currentX = e.clientX - sliderRect.left;
    const newPosition = Math.max(0, Math.min(maxDistance, currentX - 30));
    const progress = (newPosition / maxDistance) * 100;

    setHandlePosition(newPosition);
    setSliderProgress(progress);

    if (newPosition > maxDistance * 0.8) {
      setPointsClaimed(true);
      setIsDragging(false);
      setHandlePosition(maxDistance);
      setSliderProgress(100);
    }
  };

  const handleTouchMove = (e: TouchEvent): void => {
    if (!isDragging || !sliderRef.current || pointsClaimed) return;

    const sliderRect = sliderRef.current.getBoundingClientRect();
    const maxDistance = sliderRect.width - 60;
    const currentX = e.touches[0].clientX - sliderRect.left;
    const newPosition = Math.max(0, Math.min(maxDistance, currentX - 30));
    const progress = (newPosition / maxDistance) * 100;

    setHandlePosition(newPosition);
    setSliderProgress(progress);

    if (newPosition > maxDistance * 0.8) {
      setPointsClaimed(true);
      setIsDragging(false);
      setHandlePosition(maxDistance);
      setSliderProgress(100);
    }
  };

  const handleMouseUp = (): void => {
    if (
      !pointsClaimed &&
      handlePosition <
        (sliderRef.current?.getBoundingClientRect().width || 0) * 0.8
    ) {
      setHandlePosition(0);
      setSliderProgress(0);
    }
    setIsDragging(false);
  };

  const handleTouchEnd = (): void => {
    if (
      !pointsClaimed &&
      handlePosition <
        (sliderRef.current?.getBoundingClientRect().width || 0) * 0.8
    ) {
      setHandlePosition(0);
      setSliderProgress(0);
    }
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [isDragging, handlePosition, pointsClaimed]);

  const handleNewGame = (): void => {
    const totalWon = getTotalWon();
    const totalStake = getTotalStake();
    const wonGame = totalWon > 0;
    const wonAllCoins = flipResults.every((result) => result.won);
    const mixedResults =
      flipResults.some((result) => result.won) &&
      flipResults.some((result) => !result.won);

    updateStats(wonGame, wonAllCoins);

    if (checkForFourOfACoin()) {
      setShowFourOfACoinModal(true);
    } else {
      const tipData = generateTip({
        totalWins: stats.totalWins + (wonGame ? 1 : 0),
        totalGames: stats.totalGames + 1,
        consecutiveWins: wonGame ? stats.consecutiveWins + 1 : 0,
        consecutiveLosses: wonGame ? 0 : stats.consecutiveLosses + 1,
        isFirstGame: stats.totalGames === 0,
        wonAllCoins,
        mixedResults,
      });

      if (tipData) {
        setTooltipData(tipData);
        setShowTooltip(true);
      }
    }

    resetGame();
  };

  const resetGame = (): void => {
    setIsFlipping(false);
    setShowResults(false);
    setSliderProgress(0);
    setFlipResults([]);
    setPointsClaimed(false);
    setCurrentFlippingIndex(-1);
    setHandlePosition(0);
    setCoinStates([
      { id: 1, selectedSide: "heads", stake: 0 },
      { id: 2, selectedSide: "heads", stake: 0 },
      { id: 3, selectedSide: "heads", stake: 0 },
      { id: 4, selectedSide: "heads", stake: 0 },
    ]);
    setPantherBetStake(0);
    setIsPantherBetEnabled(false);
  };

  const handleFourOfACoinClose = (): void => {
    setShowFourOfACoinModal(false);

    const tipData = generateTip({
      totalWins: stats.totalWins + 1,
      totalGames: stats.totalGames + 1,
      consecutiveWins: stats.consecutiveWins + 1,
      consecutiveLosses: 0,
      isFirstGame: stats.totalGames === 0,
      wonAllCoins: true,
      mixedResults: false,
    });

    if (tipData) {
      setTooltipData(tipData);
      setShowTooltip(true);
    }
  };

  const getTotalStake = (): number => {
    return coinStates
      .filter((coin) => selectedCoins.includes(coin.id))
      .reduce((total, coin) => total + coin.stake, 0);
  };

  const getTotalWon = (): number => {
    return flipResults.reduce((total, result) => total + result.winAmount, 0);
  };

  const getFlipContainerClass = (): string => {
    const count = selectedCoins.length;
    if (count === 1) return "one-card";
    if (count === 2) return "two-cards";
    if (count === 3) return "three-cards";
    return "four-cards";
  };

  const FlipCard: React.FC<FlipCardProps> = ({
    coin,
    config,
    result,
    isCurrentlyFlipping,
    isCompleted,
  }) => (
    <div
      className={`coinflip-game__flip-card ${
        isCurrentlyFlipping ? "current-flip" : ""
      } ${isCompleted ? "completed" : ""}`}
    >
      <div
        className={`coinflip-game__flipping-coin ${
          isCurrentlyFlipping ? "flipping" : ""
        }`}
      >
        <img
          src={getCoinImageForSide(
            config,
            isCurrentlyFlipping ? coin.selectedSide : result.result
          )}
          alt={config.alt}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      </div>

      <div className="coinflip-game__stake-info">
        <div className="coinflip-game__stake-label">You Staked</div>
        <div className="coinflip-game__stake-amount">{coin.stake}</div>
        <div className="coinflip-game__bet-details">
          on{" "}
          {coin.selectedSide.charAt(0).toUpperCase() +
            coin.selectedSide.slice(1)}{" "}
          (
          {coin.selectedSide === "heads"
            ? config.headsMultiplier
            : config.tailsMultiplier}
          x)
        </div>
      </div>

      <div className="coinflip-game__win-info">
        <div className="coinflip-game__win-label">You Won</div>
        <div className="coinflip-game__win-amount">
          {isCurrentlyFlipping || (!isCompleted && currentFlippingIndex !== -1)
            ? "0"
            : result.winAmount.toFixed(2)}
        </div>
      </div>

      {isCompleted && (
        <div className="coinflip-game__result-info">
          <div className="coinflip-game__result-text">
            Result:{" "}
            {result.result.charAt(0).toUpperCase() + result.result.slice(1)}
          </div>
          <img
            src={result.won ? CupIcon : LossIcon}
            alt={result.won ? "Win" : "Loss"}
            className="coinflip-game__result-icon"
          />
        </div>
      )}
    </div>
  );

  const CoinCard: React.FC<CoinCardProps> = ({
    coin,
    config,
    showResult,
    flipResult,
  }) => (
    <div className="coinflip-game__coin-card">
      <img
        src={config.image}
        alt={config.alt}
        className="coinflip-game__coin-image"
      />

      <div className="coinflip-game__odds-section">
        <span className="coinflip-game__odds-title">Odds</span>
        <div className="coinflip-game__odds-text">
          <span>Heads ({config.headsOdds}%)</span>
          <span>Tails ({config.tailsOdds}%)</span>
        </div>
        <div className="coinflip-game__odds-bars">
          <div
            className="coinflip-game__odds-bar coinflip-game__odds-bar--heads"
            style={{ flex: config.headsOdds / 100 }}
          ></div>
          <div
            className="coinflip-game__odds-bar coinflip-game__odds-bar--tails"
            style={{ flex: config.tailsOdds / 100 }}
          ></div>
        </div>
      </div>

      <span className="coinflip-game__pick-side-text">Pick a side</span>

      <div className="coinflip-game__side-selection">
        <button
          className={`coinflip-game__side-button ${
            coin.selectedSide === "heads" ? "active" : ""
          }`}
          onClick={() => handleSideSelection(coin.id, "heads")}
        >
          <img
            src={getCoinImageForSide(config, "heads")}
            alt="Coin Heads"
            className="coinflip-game__side-button-coin"
          />
          Heads {config.headsMultiplier}x
        </button>
        <button
          className={`coinflip-game__side-button ${
            coin.selectedSide === "tails" ? "active" : ""
          }`}
          onClick={() => handleSideSelection(coin.id, "tails")}
        >
          <img
            src={getCoinImageForSide(config, "tails")}
            alt="Coin Tails"
            className="coinflip-gamec"
          />
          Tails {config.tailsMultiplier}x
        </button>
      </div>

      <div className="coinflip-game__stake-section">
        <div className="coinflip-game__stake-row">
          <span className="coinflip-game__stake-label">Your Stake</span>
          <div className="coinflip-game__stake-input-wrapper">
            <input
              type="number"
              className="coinflip-game__stake-input"
              placeholder="0"
              value={coin.stake || ""}
              onChange={(e) => handleStakeChange(coin.id, e.target.value)}
            />
            <img src={SuiLogo} alt="SUI" className="coinflip-game__sui-logo" />
          </div>
        </div>
        <div className="coinflip-game__divider"></div>
        <div className="coinflip-game__potential-win">
          <span>Potential Win</span>
          <span className="coinflip-game__potential-win-amount">
            {calculatePotentialWin(coin.stake, coin.selectedSide, coin.id)}
          </span>
        </div>
      </div>
    </div>
  );

  const ModalCoinCard: React.FC<ModalCoinCardProps> = ({
    config,
    isSelected,
  }) => (
    <div
      className={`coinflip-game__modal-coin-card ${
        isSelected ? "selected" : ""
      }`}
      style={{ borderColor: config.borderColor }}
      onClick={() => handleCoinToggle(config.id)}
    >
      <div
        className="coinflip-game__modal-coin-odds-badge"
        style={{ backgroundColor: config.borderColor }}
      >
        {config.headsOdds} : {config.tailsOdds}
      </div>
      <img
        src={config.image}
        alt={config.alt}
        className="coinflip-game__modal-coin-image"
      />
      <span className="coinflip-game__modal-coin-odds-title">Odds</span>
      <div className="coinflip-game__modal-coin-odds-text">
        Heads {config.headsMultiplier}x : Tails {config.tailsMultiplier}x
      </div>
    </div>
  );

  return (
    <div className="coinflip-game">
      <div className="coinflip-game__container">
        {/* Header with coins info and points */}
        <div className="coinflip-game__header">
          <div className="coinflip-game__left-section">
            <span className="coinflip-game__coins-text">{getCoinsText()}</span>
            <button
              className="coinflip-game__coin-selector-button"
              onClick={() => setIsModalOpen(true)}
            >
              {selectedCoins.map((coinId) => {
                const config = coinConfigs.find((c) => c.id === coinId);
                const result = flipResults.find((r) => r.coinId === coinId);
                if (!config) return null;

                if (showResults && result) {
                  return (
                    <img
                      key={coinId}
                      src={result.won ? CupIcon : LossIcon}
                      alt={result.won ? "Win" : "Loss"}
                      className="coinflip-game__coin-selector-small-image"
                    />
                  );
                }

                return (
                  <img
                    key={coinId}
                    src={config.image}
                    alt={config.alt}
                    className="coinflip-game__coin-selector-small-image"
                  />
                );
              })}
            </button>
          </div>
          <div className="coinflip-game__points-section">
            <span className="coinflip-game__points-text">Points Earned</span>
            <div className="coinflip-game__points-badge-wrapper">
              <div className="coinflip-game__points-badge">
                <img
                  src={TreasureChest}
                  alt="Treasure Chest"
                  className="coinflip-game__points-treasure-icon"
                />
                <span className="coinflip-game__points-amount">12,000,000</span>
              </div>
            </div>
          </div>
        </div>

        {/* Show original coin cards only when not flipping and not showing results */}
        {!isFlipping && !showResults && (
          <>
            {/* Coin Cards */}
            <div className="coinflip-game__coins-grid">
              {selectedCoins.map((coinId) => {
                const coin = coinStates.find((c) => c.id === coinId);
                const config = coinConfigs.find((c) => c.id === coinId);
                const result = flipResults.find((r) => r.coinId === coinId);
                if (!coin || !config) return null;
                return (
                  <CoinCard
                    key={coinId}
                    coin={coin}
                    config={config}
                    showResult={showResults}
                    flipResult={result}
                  />
                );
              })}
            </div>

            {/* Banner section */}
            <div className="coinflip-game__banner">
              <div className="coinflip-game__banner-content">
                <h2 className="coinflip-game__banner-title">Panther Bet</h2>
                <p className="coinflip-game__banner-description">
                  Parlay that your coins land on tails and win{" "}
                  <span className="coinflip-game__multiplier">50x</span> your
                  stake!
                </p>

                {/* Panther Bet Toggle */}
                <div className="coinflip-game__panther-toggle-section">
                  <label className="coinflip-game__toggle-container">
                    <input
                      type="checkbox"
                      checked={isPantherBetEnabled}
                      onChange={(e) => setIsPantherBetEnabled(e.target.checked)}
                      className="coinflip-game__toggle-input"
                      aria-label="Enable Panther Bet"
                    />
                    <span className="coinflip-game__toggle-slider"></span>
                  </label>
                  <span className="coinflip-game__toggle-text">
                    Play Panther Bet
                  </span>
                </div>
              </div>

              <img
                src={PantherBetScratch}
                alt="Panther Bet Scratch"
                className="coinflip-game__banner-scratch"
              />

              {/* Panther Bet Stake Section */}
              {isPantherBetEnabled && (
                <div className="coinflip-game__panther-stake-section">
                  <div className="coinflip-game__stake-row">
                    <span className="coinflip-game__stake-label">
                      Your Stake
                    </span>
                    <div className="coinflip-game__stake-input-wrapper">
                      <input
                        type="number"
                        className="coinflip-game__stake-input"
                        placeholder="0"
                        value={pantherBetStake || ""}
                        onChange={(e) =>
                          handlePantherBetStakeChange(e.target.value)
                        }
                      />
                      <img
                        src={SuiLogo}
                        alt="SUI"
                        className="coinflip-game__sui-logo"
                      />
                    </div>
                  </div>
                  <div className="coinflip-game__divider"></div>
                  <div className="coinflip-game__panther-bet-info">
                    <div className="coinflip-game__panther-bet-row">
                      <span>Parlay</span>
                      <span>All Tails</span>
                    </div>
                    <div className="coinflip-game__panther-bet-row">
                      <span>Total Odds</span>
                      <span>50</span>
                    </div>
                    <div className="coinflip-game__panther-bet-row">
                      <span>Potential Win</span>
                      <span className="coinflip-game__potential-win-amount">
                        {calculatePantherBetPotentialWin()}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Flip Coins Button */}
            <button
              className="coinflip-game__flip-button"
              onClick={handleFlipCoins}
            >
              Flip Coins
            </button>
          </>
        )}

        {/* Flip Animation Section */}
        {isFlipping && (
          <div className="coinflip-game__flip-container">
            <button
              className="coinflip-game__skip-button"
              onClick={handleSkipToResults}
            >
              Skip to Results
            </button>

            {/* Mobile Progress Indicator */}
            <div className="coinflip-game__flip-progress">
              {selectedCoins.map((coinId, index) => (
                <div
                  key={coinId}
                  className={`coinflip-game__progress-dot ${
                    index === currentFlippingIndex
                      ? "active"
                      : index < currentFlippingIndex
                      ? "completed"
                      : ""
                  }`}
                />
              ))}
            </div>

            <div
              className={`coinflip-game__flip-cards-container ${getFlipContainerClass()}`}
            >
              {selectedCoins.map((coinId, index) => {
                const coin = coinStates.find((c) => c.id === coinId);
                const config = coinConfigs.find((c) => c.id === coinId);
                const result = flipResults.find((r) => r.coinId === coinId);
                if (!coin || !config || !result) return null;

                return (
                  <FlipCard
                    key={coinId}
                    coin={coin}
                    config={config}
                    result={result}
                    isCurrentlyFlipping={currentFlippingIndex === index}
                    isCompleted={
                      index < currentFlippingIndex ||
                      currentFlippingIndex === -1
                    }
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Results Section */}
        {showResults && (
          <div className="coinflip-game__results-container">
            {/* Selected coins at top with result icons below each coin */}
            <div className="coinflip-game__results-coins">
              {selectedCoins.map((coinId) => {
                const config = coinConfigs.find((c) => c.id === coinId);
                const result = flipResults.find((r) => r.coinId === coinId);
                if (!config || !result) return null;
                return (
                  <div
                    key={coinId}
                    className="coinflip-game__results-coin-item"
                  >
                    <img
                      src={getCoinImageForSide(config, result.result)}
                      alt={config.alt}
                      className="coinflip-game__results-coin-image"
                    />
                    <img
                      src={result.won ? CupIcon : LossIcon}
                      alt={result.won ? "Win" : "Loss"}
                      className="coinflip-game__results-coin-result-icon"
                    />
                  </div>
                );
              })}
            </div>

            {/* Results summary - centered vertically */}
            <div className="coinflip-game__results-summary">
              <div className="coinflip-game__results-row">
                <span className="coinflip-game__results-label">
                  Total Stake
                </span>
                <span className="coinflip-game__results-amount">
                  {getTotalStake()}
                </span>
              </div>

              <div className="coinflip-game__results-row">
                <span className="coinflip-game__results-label">
                  Panther Bet
                </span>
                <span className="coinflip-game__results-amount">0</span>
              </div>

              <div className="coinflip-game__results-row">
                <span className="coinflip-game__results-label">Total Won</span>
                <span className="coinflip-game__results-amount win">
                  {getTotalWon().toFixed(2)}
                </span>
              </div>
            </div>

            <div className="coinflip-game__points-earned">
              You've earned 50 fourhead points!
            </div>

            {/* Claim slider or new game button */}
            {!pointsClaimed ? (
              <div
                ref={sliderRef}
                className={`coinflip-game__claim-slider ${
                  pointsClaimed ? "completed" : ""
                }`}
              >
                <div className="coinflip-game__claim-slider-track">
                  <div
                    className="coinflip-game__claim-slider-fill"
                    style={{ width: `${sliderProgress}%` }}
                  ></div>
                  <span
                    className={`coinflip-game__claim-slider-text ${
                      sliderProgress > 50 ? "fading" : ""
                    }`}
                  >
                    {sliderProgress < 20
                      ? "Slide to claim points"
                      : sliderProgress < 50
                      ? "Keep sliding..."
                      : sliderProgress < 80
                      ? "Almost there!"
                      : "Release to claim!"}
                  </span>
                  <div
                    className="coinflip-game__claim-slider-handle"
                    style={{ left: `${handlePosition + 4}px` }}
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                  >
                    <div className="coinflip-game__claim-slider-handle-inner"></div>
                  </div>
                  <div className="coinflip-game__claim-slider-treasure">
                    <img
                      src={TreasureChestOpen}
                      alt="Treasure Chest"
                      className="coinflip-game__claim-slider-treasure-icon"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <button
                className="coinflip-game__new-game-button"
                onClick={handleNewGame}
              >
                Start New Game
              </button>
            )}
          </div>
        )}
      </div>

      {/* Panther Bet Modal */}
      {/* Panther Bet Modal */}
      {showPantherBetModal && (
        <div className="coinflip-game__modal-overlay">
          <div className="coinflip-game__panther-bet-modal">
            <button
              className="coinflip-game__skip-animation-button"
              onClick={handleSkipPantherBetAnimation}
            >
              Skip Animation
            </button>

            <div className="coinflip-game__panther-bet-animation">
              {/* Step 1: Slash - only show during steps 1 and 2 */}
              {pantherBetAnimationStep >= 1 && pantherBetAnimationStep < 3 && (
                <img
                  src={PantherSlash}
                  alt="Panther Slash"
                  className={`coinflip-game__panther-slash ${
                    pantherBetAnimationStep === 1 ? "animate-in" : ""
                  }`}
                />
              )}

              {/* Step 2: Bear and Text */}
              {pantherBetAnimationStep >= 2 && pantherBetAnimationStep < 3 && (
                <>
                  <div className="coinflip-game__panther-congratulations">
                    <img
                      src={PantherBetText}
                      alt="Panther Bet Text"
                      className="coinflip-game__panther-bet-text animate-in"
                    />
                    <h2 className="coinflip-game__congratulations-text">
                      Congratulations, You did it!
                    </h2>
                  </div>
                  <img
                    src={PantherBear}
                    alt="Panther Bear"
                    className="coinflip-game__panther-bear animate-in"
                  />
                </>
              )}

              {/* Step 3: Show original coins and stake */}
              {pantherBetAnimationStep >= 3 && (
                <div className="coinflip-game__panther-bet-results">
                  <div className="coinflip-game__panther-bet-coins">
                    {selectedCoins.map((coinId) => {
                      const config = coinConfigs.find((c) => c.id === coinId);
                      const result = flipResults.find(
                        (r) => r.coinId === coinId
                      );
                      if (!config || !result) return null;

                      return (
                        <div
                          key={coinId}
                          className="coinflip-game__panther-bet-coin"
                        >
                          <img
                            src={
                              pantherBetAnimationStep >= 4
                                ? GoldCoin
                                : getCoinImageForSide(config, result.result)
                            }
                            alt={
                              pantherBetAnimationStep >= 4
                                ? "Gold Coin"
                                : config.alt
                            }
                            className="coinflip-game__panther-bet-coin-image"
                          />
                          {pantherBetAnimationStep >= 4 && (
                            <img
                              src={CupIcon}
                              alt="Win"
                              className="coinflip-game__panther-bet-coin-result"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="coinflip-game__panther-bet-stake-info">
                    <div className="coinflip-game__panther-bet-label">
                      You Staked
                    </div>
                    <div className="coinflip-game__panther-bet-amount">
                      {pantherBetStake}
                    </div>
                  </div>

                  {pantherBetAnimationStep >= 4 && (
                    <div className="coinflip-game__panther-bet-result-badge">
                      <span>Result:</span>
                      {Array.from({ length: 4 }).map((_, index) => (
                        <React.Fragment key={index}>
                          <span style={{ color: "#00C159" }}>Tails</span>
                          <img
                            src={CupIcon}
                            alt="Win"
                            className="coinflip-game__result-cup"
                          />
                        </React.Fragment>
                      ))}
                    </div>
                  )}

                  {pantherBetAnimationStep >= 5 && (
                    <div className="coinflip-game__panther-bet-win-info">
                      <div className="coinflip-game__panther-bet-label">
                        You Won
                      </div>
                      <div className="coinflip-game__panther-bet-win-amount">
                        {(pantherBetStake * 50).toFixed(2)}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Four of a Coin'd Modal */}
      {showFourOfACoinModal && (
        <div className="coinflip-game__modal-overlay">
          <div className="coinflip-game__four-of-a-coin-modal">
            <div className="coinflip-game__four-of-a-coin-header">
              <h2 className="coinflip-game__four-of-a-coin-title">
                Four of a Coin'd
                <img
                  src={TreasureChest}
                  alt="Treasure Chest"
                  className="coinflip-game__four-of-a-coin-treasure"
                />
              </h2>
              <p className="coinflip-game__four-of-a-coin-subtitle">
                Master Bettor!
              </p>
              <p className="coinflip-game__four-of-a-coin-subtitle-subtext">
                Congratulations on guessing all 4 coins. You just 1.5x your
                rewards. If you double your bets...get an even bigger reward.
              </p>
            </div>

            <div className="coinflip-game__four-of-a-coin-buttons">
              <button
                className="coinflip-game__four-of-a-coin-button fourcast"
                onClick={handleFourOfACoinClose}
              >
                fourcast
              </button>
              <button
                className="coinflip-game__four-of-a-coin-button cancel"
                onClick={handleFourOfACoinClose}
              >
                cancel
              </button>
            </div>

            <img
              src={PantherBear}
              alt="Panther Bear"
              className="coinflip-game__four-of-a-coin-bear"
            />
          </div>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="coinflip-game__modal-overlay">
          <div className="coinflip-game__modal">
            <div className="coinflip-game__modal-header">
              <h3 className="coinflip-game__modal-title">Choose your coins</h3>
              <button
                className="coinflip-game__modal-close"
                onClick={() => setIsModalOpen(false)}
              >
                ×
              </button>
            </div>

            <div className="coinflip-game__modal-coin-selector">
              {coinConfigs.map((config) => (
                <img
                  key={config.id}
                  src={config.image}
                  alt={config.alt}
                  className={`coinflip-game__modal-coin-selector-image ${
                    selectedCoins.includes(config.id) ? "active" : ""
                  }`}
                />
              ))}
            </div>

            <div className="coinflip-game__modal-coins-grid">
              {coinConfigs.map((config) => (
                <ModalCoinCard
                  key={config.id}
                  config={config}
                  isSelected={selectedCoins.includes(config.id)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
      <FloatingTooltip
        message={tooltipData?.message || ""}
        tip={tooltipData?.tip}
        isVisible={showTooltip}
        onClose={() => setShowTooltip(false)}
      />
    </div>
  );
}

export default CoinflipGame;
